import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, ChevronLeft, ChevronRight, GripVertical, PanelLeftClose, PanelLeftOpen, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  addDays, startOfWeek, fmtRange, pad, toLocalIso,
  WEEKDAY_INDEX, unionWorkingRanges, computeNonWorkingBlocks,
} from "@/utils/status";
import { useOutcomes } from "@/context/OutcomesContext";
import AppointmentDialog from "@/components/AppointmentDialog";
import { AvailabilityDialog } from "@/components/AvailabilityDialog";
import { useAuth } from "@/context/AuthContext";

const START_HOUR = 6;
const END_HOUR = 22;
const SLOT_MIN = 30;
const SLOTS_PER_HOUR = 60 / SLOT_MIN;
const SLOT_HEIGHT = 24;
const HOUR_HEIGHT = SLOT_HEIGHT * SLOTS_PER_HOUR;
const TOTAL_SLOTS = (END_HOUR - START_HOUR) * SLOTS_PER_HOUR;
const DRAG_SNAP_MIN = 15;

function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function slotIdxToHM(i) { return [START_HOUR + Math.floor(i / SLOTS_PER_HOUR), (i % SLOTS_PER_HOUR) * SLOT_MIN]; }
function minutesToTop(minsFromMidnight) { return (minsFromMidnight - START_HOUR * 60) * (HOUR_HEIGHT / 60); }
function dayIsoToDate(iso) { return new Date(`${iso}T00:00:00`); }

export default function CalendarPage({ calendarType = "consulenti" }) {
  const { user } = useAuth();
  const { statusesFor, metaFor, resultMetaFor, resultsFor } = useOutcomes();
  const [view, setView] = useState("day");
  const [anchor, setAnchor] = useState(new Date());
  const [users, setUsers] = useState([]);
  const [types, setTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [drag, setDrag] = useState(null);
  const [sideCollapsed, setSideCollapsed] = useState(true);
  const [overrides, setOverrides] = useState({});
  const [availTarget, setAvailTarget] = useState(null);
  const dragRef = useRef(null);
  useEffect(() => { dragRef.current = drag; }, [drag]);

  const rolesFilter = calendarType === "trainers"
    ? "manager,trainer"
    : "manager,collaborator";

  const range = useMemo(() => {
    if (view === "day") {
      const s = new Date(anchor); s.setHours(0, 0, 0, 0);
      return { start: s, end: addDays(s, 1), days: [s] };
    }
    if (view === "week") {
      const s = startOfWeek(anchor);
      return { start: s, end: addDays(s, 7), days: Array.from({ length: 7 }, (_, i) => addDays(s, i)) };
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return {
      start: gridStart, end: addDays(gridStart, 42),
      days: Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)),
      monthAnchor: first,
    };
  }, [view, anchor]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users", { params: { roles: rolesFilter, calendar: calendarType } });
      const active = data.filter((u) => u.active);
      setUsers(active);
      setSelectedIds(new Set(active.map((u) => u.id)));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [rolesFilter, calendarType]);

  const loadRefs = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([
        api.get("/appointment-types", { params: { calendar: calendarType } }),
        api.get("/clients"),
      ]);
      setTypes(t.data); setClients(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [calendarType]);

  const loadAppts = useCallback(async () => {
    if (!selectedIds.size) { setAppointments([]); return; }
    try {
      const params = {
        start: toLocalIso(range.start),
        end: toLocalIso(range.end),
        collaborator_ids: Array.from(selectedIds).join(","),
        calendar: calendarType,
      };
      const { data } = await api.get("/appointments", { params });
      setAppointments(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [range, selectedIds, calendarType]);
  const loadApptsRef = useRef(loadAppts);
  useEffect(() => { loadApptsRef.current = loadAppts; }, [loadAppts]);

  useEffect(() => { loadUsers(); loadRefs(); }, [loadUsers, loadRefs]);
  useEffect(() => { loadAppts(); }, [loadAppts]);

  const loadOverrides = useCallback(async () => {
    try {
      const { data } = await api.get("/availability-overrides", { params: { start: ymd(range.start), end: ymd(addDays(range.end, -1)) } });
      setOverrides(Object.fromEntries(data.map((o) => [`${o.user_id}|${o.date}`, o])));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [range]);
  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  // Global drag listeners
  const isDragging = drag != null;
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const d = dragRef.current; if (!d) return;
      const target = document.elementFromPoint(e.clientX, e.clientY);
      const colEl = target?.closest("[data-col-key]");
      if (!colEl) return;
      const rect = colEl.getBoundingClientRect();
      const y = Math.max(0, e.clientY - rect.top);
      const rawMin = START_HOUR * 60 + (y / HOUR_HEIGHT) * 60;
      const snappedMin = Math.max(START_HOUR * 60, Math.round(rawMin / DRAG_SNAP_MIN) * DRAG_SNAP_MIN);
      const dayIso = colEl.dataset.dayIso;
      const colCollab = colEl.dataset.collabId || "";
      const apt = d.apt;
      const durMs = new Date(apt.end) - new Date(apt.start);
      const moved =
        d.moved ||
        Math.abs(e.clientX - d.startX) > 3 ||
        Math.abs(e.clientY - d.startY) > 3;

      if (d.mode === "move") {
        const base = dayIsoToDate(dayIso);
        base.setMinutes(snappedMin);
        const newStart = base;
        const newEnd = new Date(base.getTime() + durMs);
        const overrideCollab = colCollab || apt.collaborator_id;
        setDrag((prev) => prev && { ...prev, moved,
          overrideStart: newStart.toISOString(),
          overrideEnd: newEnd.toISOString(),
          overrideCollabId: overrideCollab,
          overrideDayIso: dayIso });
      } else {
        // resize end
        const startDate = new Date(apt.start);
        const base = dayIsoToDate(dayIso);
        base.setMinutes(snappedMin);
        const minEnd = new Date(startDate.getTime() + 15 * 60 * 1000);
        const newEnd = new Date(Math.max(minEnd.getTime(), base.getTime()));
        setDrag((prev) => prev && { ...prev, moved,
          overrideStart: apt.start,
          overrideEnd: newEnd.toISOString(),
          overrideCollabId: apt.collaborator_id,
          overrideDayIso: apt.start.slice(0, 10) });
      }
    };

    const onUp = async () => {
      const d = dragRef.current; if (!d) return;
      if (!d.moved) {
        setDialogInitial(d.apt);
        setDialogOpen(true);
        setDrag(null);
        return;
      }
      try {
        const payload = {
          start: toLocalIso(new Date(d.overrideStart)),
          end: toLocalIso(new Date(d.overrideEnd)),
        };
        if (d.overrideCollabId && d.overrideCollabId !== d.apt.collaborator_id) {
          payload.collaborator_id = d.overrideCollabId;
        }
        setDrag(null);
        await api.patch(`/appointments/${d.apt.id}`, payload);
        toast.success("Appuntamento aggiornato");
        loadApptsRef.current();
      } catch (err) {
        toast.error(formatApiError(err));
        setDrag(null);
        loadApptsRef.current();
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = drag.mode === "resize" ? "ns-resize" : "grabbing";
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftBy = (n) => {
    if (view === "day") setAnchor(addDays(anchor, n));
    else if (view === "week") setAnchor(addDays(anchor, n * 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + n, 1));
  };

  const openNew = (prefill = {}) => { setDialogInitial(prefill); setDialogOpen(true); };

  const onSlotClick = (dayIso, hour, minute, collabId) => {
    const d = dayIsoToDate(dayIso);
    d.setHours(hour, minute, 0, 0);
    openNew({
      start: d.toISOString(),
      collaborator_id: collabId || (user?.role === "collaborator" ? user.id : Array.from(selectedIds)[0]),
    });
  };

  const startDrag = (apt, mode, e) => {
    if (e.button !== 0) return;
    // Permission check: only owner or admin/manager/receptionist can drag
    const isPriv = ["admin", "manager", "receptionist"].includes(user?.role);
    if (!isPriv && apt.collaborator_id !== user?.id) return;
    e.stopPropagation();
    e.preventDefault();
    setDrag({
      apt, mode,
      startX: e.clientX, startY: e.clientY,
      moved: false,
      overrideStart: apt.start,
      overrideEnd: apt.end,
      overrideCollabId: apt.collaborator_id,
      overrideDayIso: apt.start.slice(0, 10),
    });
  };

  const toggle = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const visibleUsers = users.filter((u) => selectedIds.has(u.id));

  // Build columns for TimeGrid
  const columns = useMemo(() => {
    if (view === "day") {
      if (visibleUsers.length > 1) {
        return visibleUsers.map((u) => ({
          key: `${ymd(anchor)}|${u.id}`,
          dayIso: ymd(anchor),
          day: anchor,
          collabId: u.id,
          headerLabel: u.name,
          headerSub: u.email,
          headerColor: u.color,
          workingUsers: [u],
        }));
      }
      return [{
        key: `${ymd(anchor)}|`,
        dayIso: ymd(anchor),
        day: anchor,
        collabId: "",
        headerLabel: anchor.toLocaleDateString("it-IT", { weekday: "long" }),
        headerSub: anchor.toLocaleDateString("it-IT", { day: "2-digit", month: "long" }),
        headerColor: null,
        workingUsers: visibleUsers,
      }];
    }
    if (view === "week") {
      return range.days.map((d) => ({
        key: `${ymd(d)}|`,
        dayIso: ymd(d),
        day: d,
        collabId: "",
        headerLabel: d.toLocaleDateString("it-IT", { weekday: "short" }),
        headerSub: d.getDate(),
        headerColor: null,
        workingUsers: visibleUsers,
      }));
    }
    return [];
  }, [view, anchor, visibleUsers, range.days]);

  // Apply drag override to appointments
  const effectiveApts = useMemo(() => {
    if (!drag) return appointments;
    return appointments.map((a) => {
      if (a.id !== drag.apt.id) return a;
      return { ...a, start: drag.overrideStart, end: drag.overrideEnd, collaborator_id: drag.overrideCollabId || a.collaborator_id };
    });
  }, [appointments, drag]);

  const title = view === "month"
    ? new Date(anchor.getFullYear(), anchor.getMonth(), 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" })
    : view === "day"
      ? anchor.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
      : fmtRange(range.start, addDays(range.start, 6));

  return (
    <div className="h-full flex">
      {sideCollapsed ? (
        <div className="w-10 shrink-0 border-r bg-card flex flex-col items-center py-3 gap-2 hidden lg:flex" data-testid="calendar-sidebar-collapsed">
          <button
            onClick={() => openNew()}
            title="Nuovo appuntamento"
            data-testid="create-appointment-button-mini"
            className="h-8 w-8 rounded-md bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center"
          >
            <Plus className="h-4 w-4" />
          </button>
          <div className="w-full h-px bg-border my-1" />
          <div className="flex flex-col gap-1.5 items-center overflow-y-auto flex-1 w-full px-1">
            {users.map((u) => {
              const active = selectedIds.has(u.id);
              return (
                <button
                  key={u.id}
                  onClick={() => toggle(u.id)}
                  title={u.name}
                  data-testid={`collaborator-mini-toggle-${u.id}`}
                  className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white transition-opacity ${active ? "opacity-100" : "opacity-30"}`}
                  style={{ backgroundColor: u.color }}
                >
                  {(u.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setSideCollapsed(false)}
            title="Espandi pannello filtri"
            data-testid="calendar-sidebar-expand-button"
            className="h-8 w-8 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground mt-1 border-t-0"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="w-64 shrink-0 border-r bg-card flex flex-col hidden lg:flex" data-testid="calendar-sidebar-expanded">
          <div className="flex-1 overflow-y-auto p-4">
            <Button className="w-full mb-4" onClick={() => openNew()} data-testid="create-appointment-button">
              <Plus className="h-4 w-4 mr-2" /> Nuovo Appuntamento
            </Button>

            <div>
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">Collaboratori</div>
              <div className="space-y-1">
                {users.map((u) => (
                  <label key={u.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent cursor-pointer"
                    data-testid={`collaborator-toggle-checkbox-${u.id}`}>
                    <Checkbox checked={selectedIds.has(u.id)} onCheckedChange={() => toggle(u.id)} />
                    <span className="inline-block h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: u.color }} />
                    <span className="text-sm truncate">{u.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">Legenda Stati</div>
              <div className="space-y-1.5">
                {statusesFor(calendarType).map((k) => {
                  const m = metaFor(k);
                  const Icon = m.icon;
                  return (
                    <div key={k} className="flex items-center gap-2 text-sm">
                      {k === "da_svolgere" ? (
                        <span className="inline-block h-2 w-2 mx-[3px] rounded-full bg-slate-400" />
                      ) : (
                        <Icon className={`h-3.5 w-3.5 ${m.strong ? "text-slate-700" : "text-slate-400"}`} style={{ color: m.strong ? m.color : undefined }} />
                      )}
                      <span className={m.strong ? "font-medium" : "text-muted-foreground"}>{m.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6" data-testid="legend-results">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">Legenda Esiti</div>
              <div className="space-y-1.5">
                {resultsFor(calendarType).map((k) => {
                  const m = resultMetaFor(calendarType, k);
                  const Icon = m.icon;
                  return (
                    <div key={k} className="flex items-center gap-2 text-sm">
                      <span className="inline-flex items-center justify-center h-4 w-4 rounded-sm" style={{ backgroundColor: m.color }}>
                        <Icon className="h-2.5 w-2.5 text-white" strokeWidth={2.75} />
                      </span>
                      <span className="font-medium">{m.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">Suggerimenti</div>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                <li>Trascina un evento per spostarlo.</li>
                <li>Trascina il bordo inferiore per ridimensionare.</li>
                <li>Nella vista Giorno vedi ogni collaboratore in colonna separata.</li>
              </ul>
            </div>
          </div>

          <div className="border-t p-2">
            <button
              onClick={() => setSideCollapsed(true)}
              data-testid="calendar-sidebar-collapse-button"
              title="Riduci pannello"
              className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <PanelLeftClose className="h-4 w-4" />
              <span>Riduci pannello</span>
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="h-16 border-b bg-card/80 backdrop-blur-md px-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="calendar-today-button">Oggi</Button>
            <Button variant="ghost" size="icon" onClick={() => shiftBy(-1)} data-testid="calendar-prev-date-button">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => shiftBy(1)} data-testid="calendar-next-date-button">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <h2 className="font-display text-lg font-semibold capitalize ml-2">{title}</h2>
          </div>
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="day" data-testid="calendar-view-day-button">Giorno</TabsTrigger>
              <TabsTrigger value="week" data-testid="calendar-view-week-button">Settimana</TabsTrigger>
              <TabsTrigger value="month" data-testid="calendar-view-month-button">Mese</TabsTrigger>
              <TabsTrigger value="agenda" data-testid="calendar-view-agenda-button">Agenda</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex-1 overflow-auto">
          {view === "month" && (
            <MonthGrid days={range.days} monthAnchor={range.monthAnchor} appointments={appointments} users={users}
                       onDayClick={(d) => { setAnchor(d); setView("day"); }}
                       onEventClick={(a) => { setDialogInitial(a); setDialogOpen(true); }}
                       metaFor={metaFor} />
          )}
          {view === "agenda" && (
            <AgendaList appointments={appointments} users={users} types={types} clients={clients}
                        onClick={(a) => { setDialogInitial(a); setDialogOpen(true); }}
                        metaFor={metaFor}
                        resultMetaFor={resultMetaFor}
                        calendarType={calendarType} />
          )}
          {(view === "day" || view === "week") && (
            <TimeGrid columns={columns} appointments={effectiveApts} users={users} types={types}
                      compact={view === "week"}
                      overrides={overrides}
                      onEditAvailability={view === "day" ? (u, dayIso) => setAvailTarget({ user: u, dayIso }) : null}
                      onSlotClick={onSlotClick}
                      startDrag={startDrag}
                      draggingId={drag?.apt?.id}
                      currentUser={user}
                      metaFor={metaFor}
                      resultMetaFor={resultMetaFor}
                      calendarType={calendarType} />
          )}
        </div>
      </div>

      <AppointmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={dialogInitial}
        users={users}
        types={types}
        clients={clients}
        calendar={calendarType}
        onSaved={() => { loadAppts(); }}
      />
      <AvailabilityDialog
        open={!!availTarget}
        onOpenChange={(o) => { if (!o) setAvailTarget(null); }}
        target={availTarget}
        override={availTarget ? overrides[`${availTarget.user.id}|${availTarget.dayIso}`] : undefined}
        standardSlots={availTarget ? (availTarget.user.working_hours?.[WEEKDAY_INDEX[dayIsoToDate(availTarget.dayIso).getDay()]] || []) : []}
        onSaved={loadOverrides}
      />
    </div>
  );
}

function TimeGrid({ columns, appointments, users, types, compact, overrides, onEditAvailability, onSlotClick, startDrag, draggingId, currentUser, metaFor, resultMetaFor, calendarType }) {
  const now = new Date();
  const isPriv = ["admin", "manager", "receptionist"].includes(currentUser?.role);
  const canEditHours = ["admin", "manager"].includes(currentUser?.role);
  return (
    <div className="min-w-full">
      <div className="grid" style={{ gridTemplateColumns: `64px repeat(${columns.length}, minmax(0, 1fr))` }}>
        <div className="border-b" />
        {columns.map((col) => {
          const isToday = col.dayIso === ymd(now);
          const availUser = col.collabId ? users.find((u) => u.id === col.collabId) : (col.workingUsers.length === 1 ? col.workingUsers[0] : null);
          const canEditAvail = onEditAvailability && availUser && canEditHours;
          const ov = availUser ? overrides[`${availUser.id}|${col.dayIso}`] : undefined;
          return (
            <div key={col.key} className={`relative border-b border-l px-3 py-2 text-center ${isToday && !col.headerColor ? "bg-blue-50" : ""}`}
                 style={col.headerColor ? { borderTop: `3px solid ${col.headerColor}` } : {}}>
              {canEditAvail && (
                <button
                  onClick={() => onEditAvailability(availUser, col.dayIso)}
                  title="Modifica disponibilità del giorno"
                  data-testid={`availability-edit-${availUser.id}`}
                  className="absolute top-1.5 right-1.5 h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                >
                  <Clock className="h-3.5 w-3.5" />
                </button>
              )}
              {onEditAvailability && ov && (
                <div className="text-[9px] font-bold uppercase tracking-wide text-amber-600 truncate px-6" data-testid={`availability-modified-${availUser.id}`}>{ov.reason || "Orario modificato"}</div>
              )}
              {col.headerColor && (
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: col.headerColor }} />
                  <span className="font-display font-bold text-sm truncate">{col.headerLabel}</span>
                </div>
              )}
              {!col.headerColor && (
                <>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">{col.headerLabel}</div>
                  <div className={`font-display text-xl font-bold ${isToday ? "text-blue-600" : ""}`}>{col.headerSub}</div>
                </>
              )}
              {col.headerColor && (
                <div className="text-[10px] text-muted-foreground truncate">{col.headerSub}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid" style={{ gridTemplateColumns: `64px repeat(${columns.length}, minmax(0, 1fr))` }}>
        <div>
          {Array.from({ length: TOTAL_SLOTS }).map((_, i) => {
            const [h, m] = slotIdxToHM(i);
            const isHour = m === 0;
            return (
              <div key={i} className={`relative ${isHour ? "time-cell" : "time-cell half"}`}>
                <span className={`absolute -top-2 right-1 font-mono bg-background px-1 ${isHour ? "text-[10px] text-muted-foreground" : "text-[9px] text-muted-foreground/60"}`}>
                  {pad(h)}:{isHour ? "00" : "30"}
                </span>
              </div>
            );
          })}
        </div>

        {columns.map((col) => (
          <DayColumn key={col.key} column={col} appointments={appointments} users={users} types={types} compact={compact} overrides={overrides}
                     onSlotClick={onSlotClick} startDrag={startDrag}
                     draggingId={draggingId} currentUser={currentUser}
                     metaFor={metaFor} resultMetaFor={resultMetaFor} calendarType={calendarType} />
        ))}
      </div>
    </div>
  );
}

function DayColumn({ column, appointments, users, types, compact, overrides, onSlotClick, startDrag, draggingId, currentUser, metaFor, resultMetaFor, calendarType }) {
  const dayAppts = appointments.filter((a) => {
    if (a.start.slice(0, 10) !== column.dayIso) return false;
    if (column.collabId && a.collaborator_id !== column.collabId) return false;
    return true;
  });
  const now = new Date();
  const isToday = column.dayIso === ymd(now);
  const nowTop = isToday ? ((now.getHours() * 60 + now.getMinutes() - START_HOUR * 60) * (HOUR_HEIGHT / 60)) : null;

  const layouts = layoutEvents(dayAppts);

  const weekdayKey = WEEKDAY_INDEX[column.day.getDay()];
  const effUsers = column.workingUsers.map((u) => {
    const o = overrides[`${u.id}|${column.dayIso}`];
    return o ? { ...u, working_hours: { [weekdayKey]: o.slots } } : u;
  });
  const workRanges = unionWorkingRanges(effUsers, weekdayKey);
  const nonWorking = computeNonWorkingBlocks(workRanges, START_HOUR, END_HOUR);
  const isPriv = ["admin", "manager", "receptionist"].includes(currentUser?.role);

  return (
    <div className="relative border-l"
         data-col-key={column.key}
         data-day-iso={column.dayIso}
         data-collab-id={column.collabId || ""}>
      {Array.from({ length: TOTAL_SLOTS }).map((_, i) => {
        const [h, m] = slotIdxToHM(i);
        const isHour = m === 0;
        return (
          <div key={i}
               onClick={() => onSlotClick(column.dayIso, h, m, column.collabId)}
               className={`slot-hover cursor-pointer ${isHour ? "time-cell" : "time-cell half"}`} />
        );
      })}

      {nonWorking.map((b) => {
        const top = minutesToTop(b.start);
        const height = (b.end - b.start) * (HOUR_HEIGHT / 60);
        return <div key={`${b.start}-${b.end}`} className="non-working" style={{ top, height }} />;
      })}

      {isToday && nowTop != null && nowTop >= 0 && nowTop <= TOTAL_SLOTS * SLOT_HEIGHT && (
        <div className="now-line" style={{ top: nowTop, zIndex: 4 }} />
      )}

      {layouts.map(({ a, col, cols }) => {
        const s = new Date(a.start); const e = new Date(a.end);
        const top = minutesToTop(s.getHours() * 60 + s.getMinutes());
        const height = Math.max(SLOT_HEIGHT, ((e - s) / (1000 * 60)) * (HOUR_HEIGHT / 60));
        const width = 100 / cols;
        const left = col * width;
        const usr = users.find((u) => u.id === a.collaborator_id);
        const usrColor = usr?.color || "#64748B";
        const aptType = (types || []).find((t) => t.id === a.appointment_type_id);
        const meta = metaFor(a.status);
        const Icon = meta.icon;
        const resultMeta = a.result ? resultMetaFor(calendarType, a.result) : null;
        const ResultIcon = resultMeta?.icon;
        const isCancelled = a.status === "annullato";
        const isPending = a.status === "da_svolgere";
        const strike = isCancelled ? "line-through decoration-2 decoration-slate-600/70" : "";
        const canDrag = isPriv || a.collaborator_id === currentUser?.id;
        const isDragging = draggingId === a.id;
        return (
          <div
            key={a.id}
            data-testid={`appointment-card-${a.id}`}
            onMouseDown={canDrag ? (e2) => startDrag(a, "move", e2) : undefined}
            className={`absolute rounded-md shadow-sm hover:shadow-md transition-shadow overflow-hidden group ${canDrag ? "cursor-grab" : "cursor-pointer"} ${isDragging ? "ring-2 ring-blue-500 shadow-lg z-10" : ""}`}
            style={{
              top, height, left: `calc(${left}% + 2px)`, width: `calc(${width}% - 4px)`,
              backgroundColor: `${usrColor}${isCancelled ? "0F" : "1F"}`,
              borderLeft: `4px solid ${usrColor}`,
              borderTop: `1px solid ${usrColor}66`,
              borderRight: `1px solid ${usrColor}66`,
              borderBottom: `1px solid ${usrColor}66`,
              zIndex: isDragging ? 20 : 3,
              opacity: isDragging ? 0.9 : isCancelled ? 0.7 : 1,
            }}
          >
            <div className="absolute inset-0 flex overflow-hidden py-0.5 px-1.5 gap-1.5 pointer-events-none">
              {/* LEFT: Titolo/Cliente · Tipo Appuntamento · Orario */}
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <div className="flex items-center gap-1 min-w-0">
                  {isPending && (
                    <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: usrColor }} data-testid={`appointment-pending-dot-${a.id}`} />
                  )}
                  <span className={`text-[12px] font-semibold text-slate-900 truncate ${strike}`} style={{ lineHeight: 1.15 }}>
                    {a.title}
                  </span>
                </div>
                {aptType && height >= 32 && (
                  <div className="flex items-center gap-1 min-w-0 mt-[2px]" data-testid={`appointment-type-label-${a.id}`}>
                    <span className="text-[10px] font-semibold truncate uppercase tracking-wide" style={{ color: aptType.color, lineHeight: 1.2 }}>
                      {aptType.name}
                    </span>
                  </div>
                )}
                {height >= 50 && (
                  <div className="text-[9.5px] font-mono text-slate-600 truncate mt-[2px]" style={{ lineHeight: 1.15 }}>
                    {s.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}–{e.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                    {height >= 66 && usr && !column.collabId && (
                      <span className="ml-1 font-sans font-medium" style={{ color: usrColor }}> · {usr.name}</span>
                    )}
                  </div>
                )}
              </div>
              {/* RIGHT: Stato + Esito allineati */}
              <div className="flex flex-col items-end gap-[2px] shrink-0 justify-center">
                {isPending ? null : meta.strong ? (
                  <span
                    className="inline-flex items-center gap-0.5 text-[8.5px] px-1 py-[1px] rounded-sm font-bold uppercase tracking-wide text-white shrink-0"
                    style={{ backgroundColor: meta.color, lineHeight: 1.35 }}
                    title={`Stato: ${meta.label}`}
                  >
                    <Icon className="h-2.5 w-2.5" strokeWidth={2.75} />
                    {!compact && height >= 32 && <span>{meta.label}</span>}
                  </span>
                ) : (
                  <span className="inline-flex items-center justify-center h-4 w-4 rounded-sm shrink-0"
                        style={{ backgroundColor: meta.bar || meta.color }} title={meta.label}>
                    <Icon className="h-2.5 w-2.5 text-white" strokeWidth={2.75} />
                  </span>
                )}
                {resultMeta && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[8.5px] px-1 py-[1px] rounded-sm font-bold uppercase tracking-wide text-white shrink-0"
                    style={{ backgroundColor: resultMeta.color, lineHeight: 1.35 }}
                    title={`Esito: ${resultMeta.label}`}
                    data-testid={`appointment-result-badge-${a.id}`}
                  >
                    <ResultIcon className="h-2.5 w-2.5" strokeWidth={2.75} />
                    {!compact && height >= 46 && <span>{resultMeta.label}</span>}
                  </span>
                )}
              </div>
            </div>
            {canDrag && (
              <div
                data-testid={`resize-handle-${a.id}`}
                onMouseDown={(e2) => startDrag(a, "resize", e2)}
                className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-blue-500/30"
                style={{ zIndex: 5 }}
              >
                <div className="absolute left-1/2 -translate-x-1/2 bottom-0.5 w-8 h-0.5 rounded-full bg-slate-400/50 group-hover:bg-blue-500" />
              </div>
            )}
            {canDrag && height >= 40 && (
              <GripVertical className="absolute top-1 right-1 h-3 w-3 text-slate-400/40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function layoutEvents(events) {
  const sorted = [...events].sort((a, b) => new Date(a.start) - new Date(b.start));
  const out = [];
  let cluster = [];
  const flush = () => {
    if (!cluster.length) return;
    const cols = [];
    cluster.forEach((ev) => {
      let placed = false;
      for (let i = 0; i < cols.length; i++) {
        if (new Date(cols[i][cols[i].length - 1].end) <= new Date(ev.start)) {
          cols[i].push(ev); ev._col = i; placed = true; break;
        }
      }
      if (!placed) { cols.push([ev]); ev._col = cols.length - 1; }
    });
    cluster.forEach((ev) => out.push({ a: ev, col: ev._col, cols: cols.length }));
    cluster = [];
  };
  sorted.forEach((ev) => {
    if (!cluster.length) { cluster.push(ev); return; }
    const maxEnd = Math.max(...cluster.map((x) => new Date(x.end).getTime()));
    if (new Date(ev.start).getTime() < maxEnd) cluster.push(ev);
    else { flush(); cluster.push(ev); }
  });
  flush();
  return out;
}

function MonthGrid({ days, monthAnchor, appointments, users, onDayClick, onEventClick, metaFor }) {
  const monthNum = monthAnchor?.getMonth();
  return (
    <div>
      <div className="grid grid-cols-7 border-b">
        {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map((d) => (
          <div key={d} className="px-3 py-2 text-[11px] uppercase font-bold tracking-widest text-muted-foreground border-l first:border-l-0">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const key = ymd(d);
          const dayAppts = appointments.filter((a) => a.start.slice(0, 10) === key);
          const dim = d.getMonth() !== monthNum;
          const now = new Date();
          const isToday = ymd(now) === key;
          return (
            <div key={key} className={`border-l border-b min-h-32 p-1.5 ${dim ? "bg-slate-50 text-slate-400" : "bg-card"}`}>
              <button
                onClick={() => onDayClick(d)}
                className={`text-xs font-mono font-semibold rounded px-1.5 py-0.5 ${isToday ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >{d.getDate()}</button>
              <div className="mt-1 space-y-1">
                {dayAppts.slice(0, 3).map((a) => {
                  const usr = users.find((u) => u.id === a.collaborator_id);
                  const usrColor = usr?.color || "#64748B";
                  const meta = metaFor(a.status);
                  const Icon = meta.icon;
                  const strike = a.status === "annullato" ? "line-through" : "";
                  return (
                    <button key={a.id} onClick={() => onEventClick(a)}
                      className={`w-full text-left flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] border hover:brightness-95 text-slate-800`}
                      style={{ backgroundColor: `${usrColor}1F`, borderColor: `${usrColor}55`, borderLeft: `3px solid ${usrColor}` }}>
                      <Icon className="h-3 w-3 shrink-0" style={{ color: meta.bar }} />
                      <span className={`truncate ${strike}`}>{a.title}</span>
                    </button>
                  );
                })}
                {dayAppts.length > 3 && <div className="text-[10px] text-muted-foreground pl-1">+{dayAppts.length - 3} altro</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgendaList({ appointments, users, types, clients, onClick, metaFor, resultMetaFor, calendarType }) {
  const sorted = [...appointments].sort((a, b) => new Date(a.start) - new Date(b.start));
  const groups = {};
  sorted.forEach((a) => {
    const day = a.start.slice(0, 10);
    (groups[day] = groups[day] || []).push(a);
  });

  if (!sorted.length) {
    return <div className="p-10 text-center text-muted-foreground">Nessun appuntamento nel periodo selezionato.</div>;
  }

  return (
    <div className="p-4 space-y-6">
      {Object.entries(groups).map(([day, list]) => (
        <div key={day}>
          <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">
            {new Date(day + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}
          </div>
          <div className="space-y-2">
            {list.map((a) => {
              const u = users.find((x) => x.id === a.collaborator_id);
              const t = types.find((x) => x.id === a.appointment_type_id);
              const c = clients.find((x) => x.id === a.client_id);
              const meta = metaFor(a.status);
              const Icon = meta.icon;
              const resultMeta = a.result ? resultMetaFor(calendarType, a.result) : null;
              const s = new Date(a.start), e = new Date(a.end);
              return (
                <button key={a.id} onClick={() => onClick(a)}
                        data-testid={`agenda-item-${a.id}`}
                        className={`w-full flex items-center gap-4 rounded-lg border p-3 text-left hover:shadow-sm transition`}
                        style={{ backgroundColor: `${u?.color || "#64748B"}14`, borderLeft: `4px solid ${u?.color || "#64748B"}` }}>
                  <div className="font-mono text-xs text-slate-600 w-24">
                    {s.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} – {e.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold truncate ${a.status === "annullato" ? "line-through" : ""}`}>{a.title}</div>
                    <div className="text-xs truncate" style={{ color: u?.color }}>
                      <span className="font-medium">{u?.name}</span>
                      <span className="text-muted-foreground">{t ? ` · ${t.name}` : ""}{c ? ` · ${c.name}` : ""}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm font-bold uppercase tracking-wide text-white" style={{ backgroundColor: meta.color }}>
                      <Icon className="h-3 w-3" />{meta.label}
                    </span>
                    {resultMeta && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm font-bold uppercase tracking-wide text-white" style={{ backgroundColor: resultMeta.color }}>
                        <resultMeta.icon className="h-3 w-3" />{resultMeta.label}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
